import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth-store'
import { Loading } from './feedback'
import { homePath } from './nav-config'
import { useMyPermissions } from '@/hooks/use-settings'
import { useOnboardingStatus } from '@/hooks/use-onboarding'
import {
  ONBOARDING_PATH,
  markShownThisSession,
  mayNeedOnboarding,
  shouldForceOnboarding,
  wasShownThisSession,
} from '@/lib/onboarding'
import type { Role } from '@/types/api'

/** Yêu cầu đã đăng nhập; nếu chưa → chuyển tới /login. */
export function ProtectedRoute({ children, roles }: { children: ReactNode; roles?: Role[] }) {
  const { t } = useTranslation('sharedMisc')
  const { user, loading } = useAuthStore()
  const location = useLocation()

  if (loading) return <Loading label={t('guards.loading')} className="h-screen" />
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />
  // Không đủ vai trò → về trang đầu tiên được phép (xem HomeRedirect), không mặc định Hộp thư.
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />
  return <>{children}</>
}

/**
 * `/` và đường dẫn lạ (D7): có quyền chat → Hộp thư đến; không thì trang đầu tiên
 * trong menu mà người dùng có quyền (nav-config `homePath`). Đặt bên trong
 * ProtectedRoute nên `user` đã có.
 */
export function HomeRedirect() {
  const { t } = useTranslation('sharedMisc')
  const user = useAuthStore((s) => s.user)
  const { data: perms, isLoading } = useMyPermissions()
  // Trang "Bắt đầu": chỉ chủ sở hữu (không xem hộ) mới cần hỏi trạng thái — người khác đi thẳng.
  const checkOnboarding = mayNeedOnboarding(user)
  const onboarding = useOnboardingStatus(checkOnboarding)
  if (!user) return <Navigate to="/login" replace />
  if (isLoading || (checkOnboarding && onboarding.isLoading)) return <Loading label={t('guards.loading')} className="h-screen" />
  // Người dùng mới lần đầu vào → /onboarding (một lần mỗi phiên, tới khi bỏ qua / hoàn thành). Lỗi API → bỏ qua.
  if (shouldForceOnboarding(onboarding.data, user, wasShownThisSession(user.id))) {
    markShownThisSession(user.id)
    return <Navigate to={ONBOARDING_PATH} replace />
  }
  return <Navigate to={homePath(user.role, perms)} replace />
}
